/* Xiaomi 2026 · Partner Directory · Grid + List views · Smart filters
 *
 * /xiaomi/partners
 *
 * Features:
 *   • Grid view (Ayush-style cards) ↔ List view (denser table rows)
 *   • Pagination · 50 per page
 *   • Filter chips: Returning · New Partner · Unreachable · Missing directive · Has LIVE · Has feedback · DQ issues
 *   • Sort: alphabetical · most live · most engagement · last activity · zone
 *   • Active-filter pill bar with one-click clear
 *   • All filters bookmark-able via URL search params
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import PartnerQuickPreview from '@/components/xiaomi/PartnerQuickPreview';
import OutcomeExportButton from '@/components/OutcomeExportButton';
import CsvExportModal from '@/components/xiaomi/CsvExportModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

const TIER_CHIP = {
  'Platinum Luxe':      { bg: '#fde68a', border: '#ca8a04', tone: '#713f12', icon: '★' },
  'Platinum Signature': { bg: '#f5d0fe', border: '#a855f7', tone: '#581c87', icon: '◆' },
  'Platinum Prime':     { bg: '#bfdbfe', border: '#3b82f6', tone: '#1e3a8a', icon: '●' },
  'Rising Platinum':    { bg: '#bbf7d0', border: '#16a34a', tone: '#14532d', icon: '↑' },
};

const PAGE_SIZE = 50;

const MONTH_OPTIONS = [
  { value: '', label: 'Any month · all partners' },
  { value: '2026-06', label: 'Jun 2026 · launch month' },
  { value: '2026-07', label: 'Jul 2026 · Domestic Holiday' },
  { value: '2026-08', label: 'Aug 2026 · Dinner 2' },
  { value: '2026-09', label: 'Sep 2026 · Dayout' },
  { value: '2026-10', label: 'Oct 2026 · Parents & Kids gifts (Luxe)' },
  { value: '2026-11', label: 'Nov 2026 · Diwali + Dinner 3' },
  { value: '2026-12', label: 'Dec 2026 · Year-End gift' },
];

const SORT_OPTIONS = [
  { value: 'zone_outlet', label: 'Zone · alphabetical' },
  { value: 'alpha',       label: 'Owner name A→Z' },
  { value: 'most_live',   label: 'Most LIVE workflows' },
  { value: 'least_live',  label: 'Least LIVE (idle partners)' },
  { value: 'engagement',  label: 'Most engagement / feedback' },
  { value: 'recent',      label: 'Most recent activity' },
  { value: 'due_month',   label: 'Most due in selected month' },
];


function initials(name) {
  return (name || '?').split(' ').filter(Boolean).slice(0, 2)
    .map((x) => x[0]).join('').toUpperCase();
}


function Avatar({ name, size = 'md' }) {
  const cls = size === 'sm' ? 'w-9 h-9 text-sm' : 'w-12 h-12 text-base';
  return (
    <div
      className={`${cls} rounded-full flex items-center justify-center text-white font-bold flex-shrink-0`}
      style={{ background: 'linear-gradient(135deg,#15110d,#2a2419)' }}
      data-testid="partner-avatar"
    >
      {initials(name)}
    </div>
  );
}


function TierBadge({ tier, size = 'md' }) {
  const c = TIER_CHIP[tier] || { bg: '#f3f4f6', border: '#9ca3af', tone: '#374151', icon: '·' };
  const cls = size === 'sm' ? 'px-1.5 py-0 text-[9px]' : 'px-2.5 py-0.5 text-[11px]';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-semibold ${cls}`}
      style={{ backgroundColor: c.bg, border: `1px solid ${c.border}`, color: c.tone }}
      data-testid="tier-badge"
    >
      <span>{c.icon}</span>
      {tier?.replace('Platinum ', '') || 'No tier'}
    </span>
  );
}


function StatusPill({ status_label }) {
  if (!status_label) return null;
  const isReturning = status_label.startsWith('Returning');
  const isUnreachable = status_label.startsWith('Unreachable');
  const cls = isReturning
    ? 'bg-[#f0fdf4] text-[#166534] border-[#bbf7d0]'
    : isUnreachable
    ? 'bg-[#fef2f2] text-[#991b1b] border-[#fecaca]'
    : 'bg-[#faf6ef] text-[#7c2d12] border-[#fed7aa]';
  const short = isReturning ? 'Returning' : isUnreachable ? 'Unreachable' : 'New Partner';
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cls}`}
      data-testid="status-pill"
    >
      {short}
    </span>
  );
}


function GridCard({ partner, onClick }) {
  return (
    <button
      onClick={() => onClick(partner)}
      className="w-full text-left bg-white rounded-lg border border-line p-4 hover:shadow-md hover:border-[#ff6900] transition-all"
      data-testid={`partner-row-${partner.retailer_id || partner._id}`}
    >
      <div className="flex items-center gap-4">
        <Avatar name={partner.owner_name || partner.outlet_name} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[#15110d] truncate" title={partner.owner_name}>
            {partner.owner_name || '—'}
          </div>
          <div className="text-[12px] text-soft truncate" title={partner.outlet_name}>
            {partner.outlet_name}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <TierBadge tier={partner.tier} />
            <span className="text-[11px] text-soft font-mono">
              #{partner.retailer_id || partner._id}
            </span>
          </div>
        </div>
        <div className="text-right space-y-1 flex-shrink-0">
          <div className="text-[11px] text-soft">{partner.zone} · {partner.city || '—'}</div>
          <StatusPill status_label={partner.status_label} />
          <div className="flex gap-3 justify-end text-[11px] mt-1">
            <span className="text-[#166534]" data-testid="live-count">
              LIVE <strong>{partner.live_count || 0}</strong>
            </span>
            <span className="text-soft" data-testid="done-count">
              DONE <strong>{partner.done_count || 0}</strong>
            </span>
          </div>
          {partner.due_in_month && partner.due_in_month.length > 0 && (
            <div className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border border-[#fb923c] bg-[#fff7ed] text-[#7c2d12]"
              title={partner.due_in_month.map(d => d.name).join(' · ')}
              data-testid={`due-badge-${partner.retailer_id || partner._id}`}>
              🗓 {partner.due_in_month.length} due
            </div>
          )}
        </div>
      </div>
    </button>
  );
}


function ListRow({ partner, onClick, hasDirective }) {
  return (
    <tr
      onClick={() => onClick(partner)}
      className="border-b border-line hover:bg-[#faf6ef] cursor-pointer"
      data-testid={`partner-row-${partner.retailer_id || partner._id}`}
    >
      <td className="p-2">
        <Avatar name={partner.owner_name || partner.outlet_name} size="sm" />
      </td>
      <td className="p-2">
        <div className="font-medium text-[#15110d] text-sm">{partner.owner_name || '—'}</div>
        <div className="text-[11px] text-soft">{partner.outlet_name}</div>
      </td>
      <td className="p-2 font-mono text-[11px] text-soft">{partner.retailer_id || partner._id}</td>
      <td className="p-2"><TierBadge tier={partner.tier} size="sm" /></td>
      <td className="p-2 text-[12px]">{partner.zone}<div className="text-[11px] text-soft">{partner.city || '—'}</div></td>
      <td className="p-2 text-[12px] uppercase">{partner.language_pref || '—'}</td>
      <td className="p-2"><StatusPill status_label={partner.status_label} /></td>
      <td className="p-2 text-center">
        <div className="text-[#166534] font-semibold text-sm" data-testid="live-count">{partner.live_count || 0}</div>
      </td>
      <td className="p-2 text-center">
        <div className="text-soft text-sm" data-testid="done-count">{partner.done_count || 0}</div>
      </td>
      <td className="p-2 text-center">
        {hasDirective ? (
          <span className="text-[#16a34a]" title="Standing instructions present">📌</span>
        ) : (
          <span className="text-[#b91c1c]" title="Missing directive · LSM call-blind">⚠</span>
        )}
      </td>
    </tr>
  );
}


function FilterChip({ active, onClick, count, children, tone = 'default', testId }) {
  const tones = {
    default: active ? 'bg-[#15110d] text-white' : 'bg-white text-[#15110d] border border-line hover:bg-[#faf6ef]',
    green:   active ? 'bg-[#166534] text-white' : 'bg-[#f0fdf4] text-[#166534] border border-[#bbf7d0] hover:bg-[#dcfce7]',
    amber:   active ? 'bg-[#92400e] text-white' : 'bg-[#fef3c7] text-[#92400e] border border-[#fcd34d] hover:bg-[#fef9c3]',
    red:     active ? 'bg-[#991b1b] text-white' : 'bg-[#fef2f2] text-[#991b1b] border border-[#fecaca] hover:bg-[#fee2e2]',
  };
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${tones[tone]}`}
      data-testid={testId}
    >
      {children}
      {count !== undefined && (
        <span className={`px-1.5 py-0 rounded text-[10px] ${active ? 'bg-white/20' : 'bg-black/5'}`}>
          {count}
        </span>
      )}
    </button>
  );
}


export default function XiaomiPartners() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter state · URL-backed for bookmarkability
  const [q, setQ] = useState(searchParams.get('q') || '');
  const [view, setView] = useState(searchParams.get('view') || 'grid');
  const [showExport, setShowExport] = useState(false);
  const [tier, setTier] = useState(searchParams.get('tier') || '');
  const [zone, setZone] = useState(searchParams.get('zone') || '');
  const [language, setLanguage] = useState(searchParams.get('language') || '');
  const [stateFilter, setStateFilter] = useState(searchParams.get('state') || '');
  const [statusLabel, setStatusLabel] = useState(searchParams.get('status_label') || '');
  const [hasLive, setHasLive] = useState(searchParams.get('has_live') === 'true' ? true : null);
  const [hasFeedback, setHasFeedback] = useState(searchParams.get('has_feedback') === 'true' ? true : null);
  const [missingDirective, setMissingDirective] = useState(searchParams.get('missing_directive') === 'true' ? true : null);
  const [dueMonth, setDueMonth] = useState(searchParams.get('due_month') || '');
  const [sort, setSort] = useState(searchParams.get('sort') || 'zone_outlet');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1', 10));

  // Data state
  const [rows, setRows] = useState([]);
  const [facets, setFacets] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(null);

  // Persist filter state back to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (view !== 'grid') params.set('view', view);
    if (tier) params.set('tier', tier);
    if (zone) params.set('zone', zone);
    if (language) params.set('language', language);
    if (stateFilter) params.set('state', stateFilter);
    if (statusLabel) params.set('status_label', statusLabel);
    if (hasLive) params.set('has_live', 'true');
    if (hasFeedback) params.set('has_feedback', 'true');
    if (missingDirective) params.set('missing_directive', 'true');
    if (dueMonth) params.set('due_month', dueMonth);
    if (sort !== 'zone_outlet') params.set('sort', sort);
    if (page > 1) params.set('page', String(page));
    setSearchParams(params, { replace: true });
  }, [q, view, tier, zone, language, stateFilter, statusLabel, hasLive,
      hasFeedback, missingDirective, dueMonth, sort, page, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        skip: String((page - 1) * PAGE_SIZE),
        sort,
      });
      if (q) params.set('q', q);
      if (tier) params.set('tier', tier);
      if (zone) params.set('zone', zone);
      if (language) params.set('language', language);
      if (stateFilter) params.set('state', stateFilter);
      if (statusLabel) params.set('status_label', statusLabel);
      if (hasLive) params.set('has_live', 'true');
      if (hasFeedback) params.set('has_feedback', 'true');
      if (missingDirective) params.set('missing_directive', 'true');
      if (dueMonth) params.set('due_month', dueMonth);
      const r = await axios.get(`${API}/xiaomi/dealers?${params}`, auth());
      setRows(r.data.rows || []);
      setFacets(r.data.facets);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load partners');
    } finally { setLoading(false); }
  }, [q, tier, zone, language, stateFilter, statusLabel, hasLive,
      hasFeedback, missingDirective, dueMonth, sort, page]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [q, tier, zone, language, stateFilter,
                                      statusLabel, hasLive, hasFeedback,
                                      missingDirective, dueMonth]);

  useEffect(() => { load(); }, [load]);

  const totalLive = useMemo(
    () => rows.reduce((acc, r) => acc + (r.live_count || 0), 0),
    [rows],
  );

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const activeFiltersCount = [tier, zone, language, stateFilter, statusLabel,
                                hasLive, hasFeedback, missingDirective,
                                dueMonth].filter(Boolean).length;

  const clearFilters = () => {
    setTier(''); setZone(''); setLanguage(''); setStateFilter(''); setStatusLabel('');
    setHasLive(null); setHasFeedback(null); setMissingDirective(null);
    setDueMonth('');
  };

  return (
    <div className="xiaomi-theme p-6 space-y-4" data-testid="xiaomi-partners-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl" style={{ fontFamily: 'Fraunces, serif', color: '#15110d' }}>
            Partner Directory
          </h1>
          <p className="text-sm text-soft mt-1">
            {total} {activeFiltersCount > 0 && `of ${facets?.by_tier ? Object.values(facets.by_tier).reduce((a, b) => a + b, 0) : '?'} `}
            partners · {totalLive} live workflows on this page · click any row for the 360 view
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex border border-line rounded-lg overflow-hidden bg-white"
            data-testid="view-toggle">
            <button onClick={() => setView('grid')}
              className={`px-3 py-1.5 text-[12px] ${view === 'grid' ? 'bg-[#15110d] text-white' : 'text-[#15110d] hover:bg-[#faf6ef]'}`}
              data-testid="view-toggle-grid">
              ▦ Grid
            </button>
            <button onClick={() => setView('list')}
              className={`px-3 py-1.5 text-[12px] ${view === 'list' ? 'bg-[#15110d] text-white' : 'text-[#15110d] hover:bg-[#faf6ef]'}`}
              data-testid="view-toggle-list">
              ☰ List
            </button>
          </div>
          <button onClick={() => navigate('/xiaomi/admin/dealers')}
            className="px-3 py-1.5 text-[12px] rounded border border-line bg-white hover:bg-[#faf6ef]"
            data-testid="open-admin-view-btn">
            ⚙ CRUD
          </button>
          <button onClick={() => setShowExport(true)}
            className="px-3 py-1.5 text-[12px] rounded bg-[#15803d] text-white hover:bg-[#166534] font-semibold"
            data-testid="open-csv-export-btn">
            ⬇ Export CSV
          </button>
          <OutcomeExportButton label="⬇ Outcomes CSV" defaultPreset="anniversary_status" />
        </div>
      </div>

      {/* Search + selects */}
      <div className="flex gap-2 items-center flex-wrap">
        <input
          type="text" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search name · outlet · retailer ID · city · phone"
          className="px-3 py-2 rounded border border-line text-sm bg-white flex-1 min-w-[260px] focus:outline-none focus:border-[#ff6900]"
          data-testid="search-input"
        />
        <select value={tier} onChange={(e) => setTier(e.target.value)}
          className="px-3 py-2 rounded border border-line text-sm bg-white"
          data-testid="filter-tier">
          <option value="">All tiers</option>
          {facets && Object.entries(facets.by_tier || {}).map(([t, n]) => (
            <option key={t} value={t}>{t} ({n})</option>
          ))}
        </select>
        <select value={zone} onChange={(e) => setZone(e.target.value)}
          className="px-3 py-2 rounded border border-line text-sm bg-white"
          data-testid="filter-zone">
          <option value="">All zones</option>
          {facets && Object.entries(facets.by_zone || {}).map(([z, n]) => (
            <option key={z} value={z}>{z} ({n})</option>
          ))}
        </select>
        <select value={language} onChange={(e) => setLanguage(e.target.value)}
          className="px-3 py-2 rounded border border-line text-sm bg-white"
          data-testid="filter-language">
          <option value="">All languages</option>
          {facets && Object.entries(facets.by_language || {}).map(([l, n]) => (
            <option key={l} value={l}>{l?.toUpperCase()} ({n})</option>
          ))}
        </select>
        <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}
          className="px-3 py-2 rounded border border-line text-sm bg-white"
          data-testid="filter-state">
          <option value="">All states</option>
          {facets && Object.entries(facets.by_state || {}).sort((a, b) => b[1] - a[1]).map(([s, n]) => (
            <option key={s} value={s}>{s} ({n})</option>
          ))}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)}
          className="px-3 py-2 rounded border border-line text-sm bg-white"
          data-testid="sort-select">
          {SORT_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>↕ {s.label}</option>
          ))}
        </select>
      </div>

      {/* Month-due filter · "what's due in <month>?" · prominent because it
          is the operational planning question Ayush asks every morning */}
      <div className="flex gap-2 items-center flex-wrap p-2 rounded-lg"
        style={{ background: '#fff7ed', border: '1px solid #fed7aa' }}
        data-testid="due-month-row">
        <span className="text-[11px] uppercase tracking-wider text-[#7c2d12] font-semibold">
          🗓 What's due
        </span>
        <select value={dueMonth} onChange={(e) => {
            setDueMonth(e.target.value);
            // If a month is picked, switch to the most-useful sort
            if (e.target.value && sort !== 'due_month') setSort('due_month');
          }}
          className="px-3 py-1.5 rounded border border-[#fb923c] text-sm bg-white font-medium text-[#7c2d12]"
          data-testid="filter-due-month">
          {MONTH_OPTIONS.map((m) => (
            <option key={m.value || 'any'} value={m.value}>{m.label}</option>
          ))}
        </select>
        {dueMonth && (
          <span className="text-[11px] text-[#7c2d12]">
            Showing only partners with at least one benefit due in {dueMonth.slice(-2) === '06' ? 'June' : dueMonth.slice(0, 7)} ·
            includes DOB / anniversary triggers + fixed-month benefits
          </span>
        )}
      </div>

      {/* Smart-filter chips */}
      <div className="flex gap-2 items-center flex-wrap" data-testid="smart-filter-row">
        <span className="text-[11px] text-soft uppercase tracking-wider mr-1">Quick filters:</span>
        <FilterChip
          testId="chip-returning"
          active={statusLabel === 'Returning'}
          onClick={() => setStatusLabel(statusLabel === 'Returning' ? '' : 'Returning')}
          count={facets?.by_status_rollup?.Returning}
          tone="green">
          Returning
        </FilterChip>
        <FilterChip
          testId="chip-new-partner"
          active={statusLabel === 'New Partner'}
          onClick={() => setStatusLabel(statusLabel === 'New Partner' ? '' : 'New Partner')}
          count={facets?.by_status_rollup?.['New Partner']}
          tone="amber">
          New Partner
        </FilterChip>
        <FilterChip
          testId="chip-unreachable"
          active={statusLabel === 'Unreachable'}
          onClick={() => setStatusLabel(statusLabel === 'Unreachable' ? '' : 'Unreachable')}
          count={facets?.by_status_rollup?.Unreachable}
          tone="red">
          Unreachable
        </FilterChip>
        <span className="border-l border-line h-5 mx-1" />
        <FilterChip
          testId="chip-has-live"
          active={hasLive === true}
          onClick={() => setHasLive(hasLive === true ? null : true)}
          tone="green">
          🟢 Has LIVE workflows
        </FilterChip>
        <FilterChip
          testId="chip-has-feedback"
          active={hasFeedback === true}
          onClick={() => setHasFeedback(hasFeedback === true ? null : true)}
          tone="green">
          📣 Has feedback
        </FilterChip>
        <FilterChip
          testId="chip-missing-directive"
          active={missingDirective === true}
          onClick={() => setMissingDirective(missingDirective === true ? null : true)}
          tone="red">
          ⚠ Missing directive
        </FilterChip>
        {activeFiltersCount > 0 && (
          <button onClick={clearFilters}
            className="text-[11px] text-soft hover:text-[#15110d] underline ml-2"
            data-testid="clear-filters-btn">
            Clear {activeFiltersCount} filter{activeFiltersCount > 1 ? 's' : ''}
          </button>
        )}
      </div>

      {/* Empty/loading states */}
      {loading && (
        <div className="text-center py-12 text-soft" data-testid="partners-loading">
          Loading partners…
        </div>
      )}
      {!loading && rows.length === 0 && (
        <div className="text-center py-12 text-soft" data-testid="partners-empty">
          No partners match these filters.{' '}
          {activeFiltersCount > 0 && (
            <button onClick={clearFilters} className="text-[#ff6900] underline ml-1">
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Grid view */}
      {!loading && rows.length > 0 && view === 'grid' && (
        <div
          className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-3"
          data-testid="partners-grid"
        >
          {rows.map((p) => (
            <GridCard key={p.retailer_id || p._id} partner={p}
              onClick={(prt) => setPreviewing(prt)} />
          ))}
        </div>
      )}

      {/* List view */}
      {!loading && rows.length > 0 && view === 'list' && (
        <div className="bg-white rounded-lg border border-line overflow-hidden"
          data-testid="partners-list">
          <table className="w-full text-sm">
            <thead className="bg-[#faf6ef] border-b border-line">
              <tr>
                <th className="p-2 w-12"></th>
                <th className="p-2 text-left text-[10px] uppercase tracking-wider text-soft">Owner · Outlet</th>
                <th className="p-2 text-left text-[10px] uppercase tracking-wider text-soft">Retailer ID</th>
                <th className="p-2 text-left text-[10px] uppercase tracking-wider text-soft">Tier</th>
                <th className="p-2 text-left text-[10px] uppercase tracking-wider text-soft">Zone · City</th>
                <th className="p-2 text-left text-[10px] uppercase tracking-wider text-soft">Lang</th>
                <th className="p-2 text-left text-[10px] uppercase tracking-wider text-soft">Status</th>
                <th className="p-2 text-center text-[10px] uppercase tracking-wider text-soft" title="Live workflows">LIVE</th>
                <th className="p-2 text-center text-[10px] uppercase tracking-wider text-soft" title="Closed instances">DONE</th>
                <th className="p-2 text-center text-[10px] uppercase tracking-wider text-soft" title="Directive populated?">📌</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <ListRow key={p.retailer_id || p._id} partner={p}
                  onClick={(prt) => setPreviewing(prt)}
                  hasDirective={Boolean(p.concierge_directive)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-between bg-white rounded-lg border border-line p-3"
          data-testid="pagination">
          <div className="text-[12px] text-soft">
            Page {page} of {totalPages} · showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
              className="px-3 py-1 rounded border border-line text-sm bg-white hover:bg-[#faf6ef] disabled:opacity-30"
              data-testid="page-prev">← Prev</button>
            {Array.from({ length: Math.min(7, totalPages) }, (_, idx) => {
              // Show first, last, current, and two on each side of current
              const totalNeighborhood = 7;
              let p;
              if (totalPages <= totalNeighborhood) {
                p = idx + 1;
              } else if (page <= 4) {
                p = idx + 1;
              } else if (page >= totalPages - 3) {
                p = totalPages - 6 + idx;
              } else {
                p = page - 3 + idx;
              }
              if (p < 1 || p > totalPages) return null;
              return (
                <button key={p} onClick={() => setPage(p)}
                  className={`w-9 h-8 text-[12px] rounded ${
                    p === page ? 'bg-[#15110d] text-white' : 'bg-white border border-line hover:bg-[#faf6ef]'
                  }`}
                  data-testid={`page-${p}`}>
                  {p}
                </button>
              );
            })}
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
              className="px-3 py-1 rounded border border-line text-sm bg-white hover:bg-[#faf6ef] disabled:opacity-30"
              data-testid="page-next">Next →</button>
          </div>
        </div>
      )}

      {previewing && (
        <PartnerQuickPreview
          retailerId={previewing.retailer_id || previewing._id}
          onClose={() => setPreviewing(null)}
          onOpenFull={(id) => {
            setPreviewing(null);
            navigate(`/xiaomi/partners/${id}`);
          }}
        />
      )}

      {showExport && (
        <CsvExportModal
          totalCount={total}
          filters={{
            q, tier, zone, language, state: stateFilter,
            status_label: statusLabel,
            due_month: dueMonth,
          }}
          onClose={() => setShowExport(false)}
        />
      )}
    </div>
  );
}
